// Interface sound on the landing page (docs/specs/site-sound.md §7).
//
// The synth itself is NOT here. It lives in app/services/site_sound/sound_core.js
// and is shared, byte for byte, with every audit deck — imported below for its
// side effect of registering `window.MTSound`. That is why the core is plain JS
// registering a global rather than a module with exports: a deck is a
// self-contained document with no bundler, so the same file has to work when it
// is inlined into one. Tune a voice in the core and both surfaces change.
//
// This file is only the binding layer: which site events earn a sound, and the
// SSR-safe accessors the header toggle reads.
//
// Browser-only. Installed from the CLIENT Inertia entrypoint (inertia.ts) only,
// exactly like installStandaloneRecovery and syncAnalytics — never imported by
// ssr.tsx. The core no-ops without a `window`, and every export here guards, so
// importing this module during SSR is inert.

import "../../services/site_sound/sound_core.js"

// Only these earn a sound (§7). Deliberately far fewer than feels natural:
// hover, scroll, section reveal, carousel advance, field blur and validation
// errors are all silent on purpose. Which library sound each one uses is set in
// /admin/controls, not here.
export type SoundEvent = MTSoundEvent

// Matches the core's own default when nothing is stored and reduced motion is
// not set. Used as the server snapshot, so SSR and a fresh visitor agree.
const DEFAULT_ENABLED = true

// Last settings seen from Inertia's shared props. Held here so `core()` can pass
// them on the very first call, which is what creates the instance — otherwise
// the first sound of a session would use the fallback config.
let settings: Partial<MTSoundConfig> | undefined

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function core(): MTSoundInstance | null {
  if (!isBrowser()) return null
  const registry = window.MTSound
  if (!registry) return null
  try {
    return registry.create(settings)
  } catch {
    return null
  }
}

export function playSound(event: SoundEvent): void {
  core()?.play(event)
}

/** Audition a library sound by id. Used only by /admin/controls. */
export function previewSound(soundId: string): void {
  core()?.preview(soundId)
}

/**
 * Sitewide kill switch. False means the visitor's toggle should not render at
 * all — a control that cannot do anything is worse than no control.
 */
export function isSoundAvailable(): boolean {
  return core()?.available() ?? DEFAULT_ENABLED
}

export function isSoundEnabled(): boolean {
  return core()?.enabled() ?? DEFAULT_ENABLED
}

export function setSoundEnabled(value: boolean): void {
  core()?.setEnabled(value)
}

/** Subscribe to preference changes (including from another tab). */
export function subscribeSound(listener: () => void): () => void {
  const instance = core()
  if (!instance) return () => {}
  return instance.onChange(listener)
}

/** The server has no preference to read, so it renders the default. */
export function getServerSoundSnapshot(): boolean {
  return DEFAULT_ENABLED
}

// --- delegated CTA clicks ---------------------------------------------------
// One capture-phase listener for the whole page. Anything carrying
// `data-sound="<voice>"` plays that voice when clicked, so tagging a new CTA
// needs no code change here.
//
// Deliberately a separate attribute from analytics' `data-track-click`:
// analytics tracks far more of the page than deserves a sound.
function onClick(e: MouseEvent): void {
  const target = e.target
  if (!(target instanceof Element)) return
  const el = target.closest<HTMLElement>("[data-sound]")
  const event = el?.dataset.sound
  if (event) playSound(event as SoundEvent)
}

let installed = false

// Called from inertia.ts on the initial load and again on every Inertia
// navigation, so saving /admin/controls takes effect immediately rather than on
// the next full page load. The click listener is delegated and wired once.
export function syncSound(next: Partial<MTSoundConfig> | undefined): void {
  if (!isBrowser()) return
  if (next) {
    settings = next
    // Only if an instance already exists — otherwise `core()` will pass these
    // in at construction and setConfig would be a no-op on nothing.
    if (window.MTSound && installed) core()?.setConfig(next)
  }
  if (installed) return
  installed = true
  document.addEventListener("click", onClick, true)
}
